import { PLAIN_NUMBER_FORMAT } from '../../config/metrics';
import type { MetricFormat } from '../../config/metrics';
import type { PromoLibraryKpiFormat } from '../../config/promoLibraryConfig';
import { formatMetricValue } from '../../utils/formatMetricValue';

export const EMPTY_VALUE = '--';

const toNumber = (value: number | string | boolean | null | undefined): number | null => {
    if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
    const numeric = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

/**
 * Formats a KPI value per its column config. Non-numeric input renders as text.
 *
 * Numeric values go through the shared `formatMetricValue`, so the configured
 * `prefix` / `suffix` are rendered literally rather than being reduced to a
 * signal for the tenant currency — which is what previously made a
 * `prefix: "€"` KPI render `$`, and dropped every suffix other than `%`.
 */
export function formatKpiValue(
    value: number | string | boolean | null | undefined,
    format: PromoLibraryKpiFormat,
    valueFormat: MetricFormat = PLAIN_NUMBER_FORMAT,
): string {
    // Matches the edit modal's True/False wording so the cell and the dialog
    // describe a boolean KPI the same way.
    if (typeof value === 'boolean') return value ? 'True' : 'False';

    if (format === 'text') {
        return value === null || value === undefined || value === '' ? EMPTY_VALUE : String(value);
    }

    const numeric = toNumber(value);
    if (numeric === null) return EMPTY_VALUE;

    return formatMetricValue(numeric, valueFormat);
}

/**
 * Percentage delta between the baseline and the current value. Null whenever the
 * change is undefined rather than zero: a missing side, or a zero baseline (where
 * the percentage is infinite and would read as a bogus spike).
 */
export function computeChangePercent(
    current: number | string | boolean | null | undefined,
    previous: number | string | boolean | null | undefined,
): number | null {
    const currentValue = toNumber(current);
    const previousValue = toNumber(previous);
    if (currentValue === null || previousValue === null || previousValue === 0) return null;
    return ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
}

/** Substitutes `{loaded}` / `{total}` in the configured record-count label. */
export function formatRecordCount(template: string, loaded: number, total: number): string {
    return template
        .replace('{loaded}', formatMetricValue(loaded, PLAIN_NUMBER_FORMAT))
        .replace('{total}', formatMetricValue(total, PLAIN_NUMBER_FORMAT));
}
